/**
 * Converts between our backend survey format and SurveyJS Creator schema
 * so we can use the drag-and-drop creator but keep API/mobile unchanged.
 */

function toNum(val, fallback) {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  const n = Number(val);
  return Number.isNaN(n) ? fallback : n;
}

/**
 * Map our question type to SurveyJS element type and build element config
 */
function ourQuestionToSurveyJSElement(q, index) {
  const name = `q_${q.id ?? index}`;
  const base = {
    name,
    title: q.text || 'Question',
    isRequired: !!q.required,
  };
  if (q.helperText) base.description = q.helperText;

  switch (q.type) {
    case 'short_answer': {
      const inputType = (q.inputType != null && String(q.inputType).trim() !== '') ? String(q.inputType).toLowerCase() : 'text';
      return { ...base, type: 'text', inputType };
    }
    case 'paragraph':
      return { ...base, type: 'comment' };
    case 'multiple_choice': {
      const choices = (q.options || []).map((opt, i) =>
        typeof opt === 'string' ? { value: `opt_${i}`, text: opt } : { value: opt.value || `opt_${i}`, text: opt.text || opt }
      );
      const isDropdown = q.choiceType === 'dropdown';
      const el = {
        ...base,
        type: isDropdown ? 'dropdown' : 'radiogroup',
        choices: choices.length ? choices : [{ value: '1', text: 'Option 1' }],
      };
      if (q.showOtherItem) {
        el.showOtherItem = true;
        el.otherText = q.otherText || 'Other';
      }
      return el;
    }
    case 'checkboxes': {
      const checkChoices = (q.options || []).map((opt, i) =>
        typeof opt === 'string' ? { value: `opt_${i}`, text: opt } : { value: opt.value || `opt_${i}`, text: opt.text || opt }
      );
      const checkEl = { ...base, type: 'checkbox', choices: checkChoices.length ? checkChoices : [{ value: '1', text: 'Option 1' }] };
      if (q.showOtherItem) {
        checkEl.showOtherItem = true;
        checkEl.otherText = q.otherText || 'Other';
      }
      return checkEl;
    }
    case 'yes_no':
      return {
        ...base,
        type: 'boolean',
        valueTrue: q.valueTrue != null && String(q.valueTrue).trim() !== '' ? String(q.valueTrue).trim() : 'Yes',
        valueFalse: q.valueFalse != null && String(q.valueFalse).trim() !== '' ? String(q.valueFalse).trim() : 'No',
      };
    case 'number': {
      // Single-line input with number format: keep as type "text" + inputType "number" so Creator shows it as Single line input
      const numEl = { ...base, type: 'text', inputType: 'number' };
      if (typeof q.minValue === 'number') numEl.min = q.minValue;
      if (typeof q.maxValue === 'number') numEl.max = q.maxValue;
      if (typeof q.step === 'number') numEl.step = q.step;
      return numEl;
    }
    case 'date':
      // Single-line input with date format: keep as type "text" + inputType "date" so Creator shows it as Single line input
      return { ...base, type: 'text', inputType: 'date' };
    case 'time':
      return { ...base, type: 'text', inputType: 'time' };
    case 'scale':
      if (String(q.scaleDisplay || '').toLowerCase() === 'slider') {
        return {
          ...base,
          type: 'slider',
          min: typeof q.scaleMin === 'number' ? q.scaleMin : 0,
          max: typeof q.scaleMax === 'number' ? q.scaleMax : 100,
          step: typeof q.step === 'number' ? q.step : 1,
          ...(q.scaleMinLabel != null && String(q.scaleMinLabel).trim() !== '' && { minRateDescription: String(q.scaleMinLabel).trim() }),
          ...(q.scaleMaxLabel != null && String(q.scaleMaxLabel).trim() !== '' && { maxRateDescription: String(q.scaleMaxLabel).trim() }),
        };
      }
      return {
        ...base,
        type: 'rating',
        rateMin: typeof q.scaleMin === 'number' ? q.scaleMin : 1,
        rateMax: typeof q.scaleMax === 'number' ? q.scaleMax : 5,
        ...(q.scaleMinLabel != null && String(q.scaleMinLabel).trim() !== '' && { minRateDescription: String(q.scaleMinLabel).trim() }),
        ...(q.scaleMaxLabel != null && String(q.scaleMaxLabel).trim() !== '' && { maxRateDescription: String(q.scaleMaxLabel).trim() }),
      };
    case 'matrix': {
      const rows = (q.rows || []).map((r, i) => (typeof r === 'string' ? { value: `r_${i}`, text: r } : { value: r.value || `r_${i}`, text: r.text || r }));
      const columns = (q.columns || []).map((c, i) => (typeof c === 'string' ? { value: `c_${i}`, text: c } : { value: c.value || `c_${i}`, text: c.text || c }));
      return {
        ...base,
        type: 'matrix',
        rows: rows.length ? rows : [{ value: '1', text: 'Row 1' }],
        columns: columns.length ? columns : [{ value: '1', text: 'Column 1' }],
      };
    }
    default:
      return { ...base, type: 'text' };
  }
}

/**
 * Convert our API survey (name, description, status, questions[]) to SurveyJS JSON
 *
 * Supports multi-page by honoring per-question pageIndex (0-based).
 * Older surveys without pageIndex are placed on page 0.
 */
export function ourSurveyToSurveyJS(survey) {
  let raw = survey.questions;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }
  const questions = Array.isArray(raw)
    ? raw
    : raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? [raw]
      : [];

  if (!questions.length) {
    return {
      title: survey.name || 'Survey',
      description: survey.description || '',
      pages: [
        {
          name: 'page1',
          title: survey.name || 'Page 1',
          elements: [],
        },
      ],
    };
  }

  // Group questions by pageIndex (default 0) to rebuild SurveyJS pages
  const pagesMap = new Map();
  questions.forEach((q, idx) => {
    const pageIndex = typeof q.pageIndex === 'number' && q.pageIndex >= 0 ? q.pageIndex : 0;
    if (!pagesMap.has(pageIndex)) {
      pagesMap.set(pageIndex, []);
    }
    pagesMap.get(pageIndex).push({ question: q, originalIndex: idx });
  });

  const sortedPageIndices = Array.from(pagesMap.keys()).sort((a, b) => a - b);

  const pages = sortedPageIndices.map((pageIndex, visualIndex) => {
    const items = pagesMap.get(pageIndex) || [];
    const firstQuestion = items[0]?.question;
    const pageTitle =
      firstQuestion?.pageTitle != null && String(firstQuestion.pageTitle).trim() !== ''
        ? String(firstQuestion.pageTitle).trim()
        : visualIndex === 0
          ? survey.name || `Page ${pageIndex + 1}`
          : `Page ${pageIndex + 1}`;
    const elements = items
      .map((item, localIdx) => ourQuestionToSurveyJSElement(item.question, item.originalIndex ?? localIdx))
      .filter((el) => el != null && (el.type || el.name));
    return {
      name: `page${pageIndex + 1}`,
      title: pageTitle,
      elements,
    };
  });

  return {
    title: survey.name || 'Survey',
    description: survey.description || '',
    pages,
  };
}

/**
 * Recursively collect all question elements from a page (including inside panels).
 */
function collectElements(elements) {
  const out = [];
  (elements || []).forEach((el) => {
    const type = (el.type != null ? String(el.type) : '').toLowerCase();
    if (type === 'panel' || type === 'paneldynamic') {
      out.push(...collectElements(el.elements));
    } else {
      out.push(el);
    }
  });
  return out;
}

/**
 * Map SurveyJS element to our question shape
 */
function surveyJSElementToOurQuestion(el, index) {
  const type = (el.type != null ? String(el.type) : 'text').toLowerCase();
  const id = el.name && String(el.name).startsWith('q_') ? parseInt(String(el.name).replace('q_', ''), 10) : Date.now() + index;
  const q = {
    id: Number.isNaN(id) ? Date.now() + index : id,
    text: el.title || '',
    required: !!el.isRequired,
    helperText: el.description || '',
  };

  switch (type) {
    case 'text': {
      const rawInput = el.inputType != null ? el.inputType : 'text';
      const inputType = typeof rawInput === 'string' ? rawInput.toLowerCase() : 'text';
      if (inputType === 'number') q.type = 'number';
      else if (inputType === 'date' || inputType === 'datetime-local' || inputType === 'datetime') q.type = 'date';
      else {
        q.type = 'short_answer';
        if (inputType !== 'text') q.inputType = inputType;
      }
      break;
    }
    case 'comment':
      q.type = 'paragraph';
      break;
    case 'radiogroup':
      q.type = 'multiple_choice';
      q.options = (el.choices || []).map((c) => (typeof c === 'string' ? c : c.text || c.value));
      if (el.showOtherItem) {
        q.showOtherItem = true;
        q.otherText = el.otherText != null && String(el.otherText).trim() !== '' ? String(el.otherText).trim() : 'Other';
      }
      break;
    case 'checkbox':
      q.type = 'checkboxes';
      q.options = (el.choices || []).map((c) => (typeof c === 'string' ? c : c.text || c.value));
      if (el.showOtherItem) {
        q.showOtherItem = true;
        q.otherText = el.otherText != null && String(el.otherText).trim() !== '' ? String(el.otherText).trim() : 'Other';
      }
      break;
    case 'boolean':
      q.type = 'yes_no';
      if (el.valueTrue != null) q.valueTrue = String(el.valueTrue).trim() || 'Yes';
      if (el.valueFalse != null) q.valueFalse = String(el.valueFalse).trim() || 'No';
      break;
    case 'number':
      q.type = 'number';
      if (typeof el.min === 'number') q.minValue = el.min;
      if (typeof el.max === 'number') q.maxValue = el.max;
      if (typeof el.step === 'number') q.step = el.step;
      break;
    case 'date':
      q.type = 'date';
      break;
    case 'rating':
      q.type = 'scale';
      q.scaleMin = el.rateMin ?? 1;
      q.scaleMax = el.rateMax ?? 5;
      if (el.minRateDescription != null && String(el.minRateDescription).trim() !== '') q.scaleMinLabel = String(el.minRateDescription).trim();
      if (el.maxRateDescription != null && String(el.maxRateDescription).trim() !== '') q.scaleMaxLabel = String(el.maxRateDescription).trim();
      break;
    case 'slider':
      q.type = 'scale';
      q.scaleDisplay = 'slider';
      q.scaleMin = toNum(el.min, 0);
      q.scaleMax = toNum(el.max, 100);
      q.step = toNum(el.step, 1);
      if (el.minRateDescription != null && String(el.minRateDescription).trim() !== '') q.scaleMinLabel = String(el.minRateDescription).trim();
      if (el.maxRateDescription != null && String(el.maxRateDescription).trim() !== '') q.scaleMaxLabel = String(el.maxRateDescription).trim();
      if (Array.isArray(el.customLabels) && el.customLabels.length > 0) q.sliderCustomLabels = el.customLabels;
      break;
    case 'matrix':
      q.type = 'matrix';
      q.rows = (el.rows || []).map((r) => (typeof r === 'string' ? r : r.text || r.value));
      q.columns = (el.columns || []).map((c) => (typeof c === 'string' ? c : c.text || c.value));
      break;
    case 'dropdown':
      q.type = 'multiple_choice';
      q.choiceType = 'dropdown';
      q.options = (el.choices || []).map((c) => (typeof c === 'string' ? c : c.text || c.value));
      if (el.showOtherItem) {
        q.showOtherItem = true;
        q.otherText = el.otherText != null && String(el.otherText).trim() !== '' ? String(el.otherText).trim() : 'Other';
      }
      break;
    default:
      q.type = 'short_answer';
  }
  return q;
}

/**
 * Convert SurveyJS JSON (from Creator) to our API survey shape
 */
export function surveyJSToOurSurvey(surveyJsJson, existingSurvey = {}) {
  const pages = surveyJsJson?.pages || [];
  const questions = [];
  pages.forEach((page, pageIndex) => {
    const pageTitle = page.title != null ? String(page.title).trim() : '';
    const flatElements = collectElements(page.elements || []);
    flatElements.forEach((el, idx) => {
      const q = surveyJSElementToOurQuestion(el, questions.length + idx);
      q.pageIndex = pageIndex;
      if (idx === 0 && pageTitle !== '') q.pageTitle = pageTitle;
      questions.push(q);
    });
  });
  const out = {
    name: surveyJsJson?.title ?? existingSurvey.name ?? 'Survey',
    description: surveyJsJson?.description ?? existingSurvey.description ?? '',
    status: existingSurvey.status ?? 'draft',
    questions,
  };
  if (existingSurvey.id != null) out.id = existingSurvey.id;
  return out;
}
